export type BroadcastStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled';
export type BroadcastChannel = 'internal' | 'email';

export interface BroadcastFilters {
  audience: 'all' | 'plan' | 'status' | 'inactive' | 'no_onboarding' | 'with_goals' | 'without_goals' | 'with_transactions' | 'without_transactions' | 'not_accessed';
  plan_id?: string;
  subscription_status?: 'active' | 'trial' | 'overdue' | 'cancelled';
  inactive_days?: number;
}

export interface Broadcast {
  id: string;
  title: string;
  subtitle: string | null;
  content: string;
  image_url: string | null;
  cta_label: string | null;
  cta_url: string | null;
  channels: BroadcastChannel[];
  filters: BroadcastFilters;
  status: BroadcastStatus;
  scheduled_at: string | null;
  sent_at: string | null;
  created_by: string | null;
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  read_count: number;
  created_at: string;
  updated_at: string;
}

export const BROADCAST_STATUS_LABEL: Record<BroadcastStatus, { label: string; color: string; bg: string; icon: string }> = {
  draft:      { label: 'Rascunho',   color: 'text-white/50',    bg: 'bg-white/10',    icon: 'ri-draft-line' },
  scheduled:  { label: 'Agendado',   color: 'text-amber-400',   bg: 'bg-amber-500/15', icon: 'ri-calendar-schedule-line' },
  sending:    { label: 'Enviando',   color: 'text-sky-400',     bg: 'bg-sky-500/15',  icon: 'ri-send-plane-fill' },
  sent:       { label: 'Enviado',    color: 'text-emerald-400', bg: 'bg-emerald-500/15', icon: 'ri-checkbox-circle-line' },
  cancelled:  { label: 'Cancelado',  color: 'text-red-400',     bg: 'bg-red-500/15',  icon: 'ri-close-circle-line' },
};

export const AUDIENCE_OPTIONS: { value: BroadcastFilters['audience']; label: string; icon: string; description: string }[] = [
  { value: 'all',               label: 'Todos os clientes',          icon: 'ri-group-3-line',           description: 'Toda a base de usuários' },
  { value: 'plan',              label: 'Por plano',                  icon: 'ri-price-tag-3-line',       description: 'Filtra por plano específico' },
  { value: 'status',            label: 'Por status da assinatura',   icon: 'ri-file-list-3-line',       description: 'Trial, ativo, inadimplente...' },
  { value: 'inactive',          label: 'Inativos há X dias',         icon: 'ri-time-line',              description: 'Usuários sem acesso recente' },
  { value: 'no_onboarding',     label: 'Sem onboarding completo',    icon: 'ri-user-unfollow-line',     description: 'Não finalizaram o cadastro' },
  { value: 'with_goals',        label: 'Com metas',                  icon: 'ri-focus-3-line',           description: 'Usuários que têm metas criadas' },
  { value: 'without_goals',     label: 'Sem metas',                  icon: 'ri-target-line',            description: 'Usuários sem nenhuma meta' },
  { value: 'with_transactions', label: 'Com lançamentos',            icon: 'ri-exchange-dollar-line',   description: 'Usuários que já lançaram' },
  { value: 'without_transactions', label: 'Sem lançamentos',         icon: 'ri-wallet-2-line',          description: 'Usuários que nunca lançaram' },
];

export const EMOJI_LIST = [
  '🎉','🚀','💡','⭐','🔥','✅','📢','💰','🎯','🏆',
  '📊','💬','🙌','❤️','🎁','🛡️','💎','📈','⚡','🤝',
  '📌','💥','🌟','🔔','🎀','📱','🖥️','✨','🏅','👏',
];

export const EMPTY_BROADCAST: Omit<Broadcast, 'id' | 'created_at' | 'updated_at' | 'sent_at' | 'created_by' | 'total_recipients' | 'sent_count' | 'failed_count'> = {
  title: '',
  subtitle: null,
  content: '',
  image_url: null,
  cta_label: null,
  cta_url: null,
  channels: ['internal'],
  filters: { audience: 'all' },
  status: 'draft',
  scheduled_at: null,
};
